import { useFlag } from "@dashboard/featureFlags";
import { useFileUploadMutation } from "@dashboard/graphql";
import useNotifier from "@dashboard/hooks/useNotifier";
import { errorMessages } from "@dashboard/intl";
import {
  richTextToHtml,
  serializeTinyMceHtmlForApi,
} from "@dashboard/utils/richText/compat";
import {
  RichTextDocument,
  RichTextEditorRef,
  RichTextValue,
} from "@dashboard/utils/richText/types";
import { LogLevels } from "@editorjs/editorjs";
import { FormControl, FormHelperText } from "@material-ui/core";
import { useId } from "@reach/auto-id";
import { EditorCore, Props as ReactEditorJSProps } from "@react-editor-js/core";
import { Box } from "@saleor/macaw-ui-next";
import { Editor as TinyMceEditor } from "@tinymce/tinymce-react";
import clsx from "clsx";
import React from "react";
import { useIntl } from "react-intl";

import { tools } from "./consts";
import { useHasRendered, useUpdateOnRerender } from "./hooks";
import { ReactEditorJS } from "./ReactEditorJS";
import useStyles from "./styles";

export type EditorJsProps = Omit<ReactEditorJSProps, "factory">;

export interface RichTextEditorProps extends Omit<EditorJsProps, "onChange" | "defaultValue"> {
  id?: string;
  disabled: boolean;
  error: boolean;
  helperText?: string;
  label: string;
  name: string;
  defaultValue?: RichTextValue;
  editorRef:
    | React.RefCallback<RichTextEditorRef>
    | React.MutableRefObject<RichTextEditorRef | null>
    | null;
  onChange?: (data?: RichTextDocument | null) => void;
  onBlur?: () => void;
}

const TINYMCE_IMAGE_MAX_SIZE = 10 * 1024 * 1024;
const TINYMCE_PLUGINS = ["autolink", "code", "image", "link", "lists", "table"];
const TINYMCE_TOOLBAR =
  "undo redo | blocks | bold italic strikethrough | link image blockquote code | bullist numlist | table tabledelete tableprops tablerowprops tablecellprops | tableinsertrowbefore tableinsertrowafter tabledeleterow tableinsertcolbefore tableinsertcolafter tabledeletecol | removeformat";

const assignEditorRef = (
  editorRef: RichTextEditorProps["editorRef"],
  editor: RichTextEditorRef | null,
) => {
  if (typeof editorRef === "function") {
    editorRef(editor);

    return;
  }

  if (editorRef) {
    editorRef.current = editor;
  }
};

const getEditorJsDefaultValue = (value: RichTextValue) =>
  value && typeof value === "object" && "blocks" in value ? value : { blocks: [] };

const isImageFile = (file: Blob) => file.type.startsWith("image/");

const toUploadFile = (blobInfo: any) => {
  const blob = blobInfo.blob();
  const filename = blobInfo.filename();

  return blob instanceof File
    ? blob
    : new File([blob], filename, { type: blob.type || "application/octet-stream" });
};

const EditorJsRichTextEditor = ({
  id: defaultId,
  disabled,
  error,
  label,
  name,
  helperText,
  editorRef,
  onInitialize,
  onChange,
  onBlur,
  defaultValue,
  ...props
}: RichTextEditorProps) => {
  const classes = useStyles({});
  const id = useId(defaultId);
  const ref = React.useRef<EditorCore | null>(null);
  const hasBeenFocusedRef = React.useRef(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const [hasValue, setHasValue] = React.useState(false);
  const editorJsDefaultValue = getEditorJsDefaultValue(defaultValue);
  const isTyped = Boolean(hasValue || isFocused);
  const handleInitialize = React.useCallback(
    (editor: EditorCore) => {
      onInitialize?.(editor);
      ref.current = editor;
      assignEditorRef(editorRef, editor as unknown as RichTextEditorRef);
    },
    [editorRef, onInitialize],
  );
  const hasRendered = useHasRendered();

  useUpdateOnRerender({
    render: ref.current?.render.bind(ref.current),
    defaultValue: editorJsDefaultValue,
    hasRendered,
  });

  React.useEffect(() => () => assignEditorRef(editorRef, null), [editorRef]);

  return (
    <FormControl
      data-test-id={"rich-text-editor-" + name}
      disabled={disabled}
      error={error}
      fullWidth
      variant="outlined"
    >
      <Box
        as="label"
        color={error ? "critical2" : "default2"}
        fontWeight="regular"
        size={1}
        position="absolute"
        htmlFor={id}
        zIndex="2"
        __top={9}
        __left={9}
      >
        {label}
      </Box>
      {hasRendered && (
        <ReactEditorJS
          holder={id}
          tools={tools}
          logLevel={"ERROR" as LogLevels.ERROR}
          onInitialize={handleInitialize}
          onChange={async event => {
            const editorJsValue = await event.saver.save();

            setHasValue(editorJsValue.blocks.length > 0);

            // EditorJS fires onChange once while normalizing blocks right after
            // it finishes loading its initial content, even with no user edit.
            // Ignore that until the editor has actually been focused, so we
            // don't mark the form dirty (and trip the unsaved-changes prompt)
            // before the user has touched anything.
            if (!hasBeenFocusedRef.current) {
              return;
            }

            return onChange?.(editorJsValue);
          }}
          defaultValue={editorJsDefaultValue}
          {...props}
        >
          <div
            id={id}
            className={clsx(classes.editor, classes.root, {
              [classes.rootErrorFocus]: isFocused && error,
              [classes.rootActive]: isFocused,
              [classes.rootDisabled]: disabled,
              [classes.rootError]: error,
              [classes.rootHasLabel]: label !== "",
              [classes.rootTyped]: isTyped || editorJsDefaultValue.blocks.length > 0,
            })}
            onFocus={() => {
              hasBeenFocusedRef.current = true;
              setIsFocused(true);
            }}
            onBlur={() => {
              setIsFocused(false);
              onBlur?.();
            }}
          />
        </ReactEditorJS>
      )}
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

const TinyMceRichTextEditor = ({
  id: defaultId,
  disabled,
  error,
  label,
  name,
  helperText,
  editorRef,
  onChange,
  onBlur,
  defaultValue,
  readOnly,
}: RichTextEditorProps) => {
  const classes = useStyles({});
  const id = useId(defaultId);
  const intl = useIntl();
  const notify = useNotifier();
  const [uploadFile] = useFileUploadMutation({});
  const initialHtml = React.useMemo(() => richTextToHtml(defaultValue), [defaultValue]);
  const editorInstanceRef = React.useRef<any>(null);
  const lastRenderedHtmlRef = React.useRef(initialHtml);
  const isProgrammaticChangeRef = React.useRef(false);
  const hasBeenFocusedRef = React.useRef(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const [hasValue, setHasValue] = React.useState(Boolean(initialHtml));
  const isReadOnly = Boolean(disabled || readOnly);
  const isTyped = Boolean(hasValue || isFocused);
  const imageUploadErrorText = intl.formatMessage(errorMessages.imageUploadErrorText);
  const notifyImageUploadError = React.useCallback(() => {
    notify({
      status: "error",
      title: intl.formatMessage(errorMessages.imgageUploadErrorTitle),
      text: imageUploadErrorText,
    });
  }, [imageUploadErrorText, intl, notify]);

  const validateImageFile = React.useCallback(
    (file: Blob) => {
      if (!isImageFile(file) || file.size > TINYMCE_IMAGE_MAX_SIZE) {
        notifyImageUploadError();

        throw new Error(imageUploadErrorText);
      }
    },
    [imageUploadErrorText, notifyImageUploadError],
  );

  const uploadTinyMceImage = React.useCallback(
    async (blobInfo: any, progress: (percent: number) => void) => {
      const file = toUploadFile(blobInfo);

      validateImageFile(file);
      progress(1);

      try {
        const { data } = await uploadFile({ variables: { file } });
        const errors = data?.fileUpload?.errors ?? [];
        const url = data?.fileUpload?.uploadedFile?.url;

        if (errors.length || !url) {
          throw new Error(imageUploadErrorText);
        }

        progress(100);

        return url;
      } catch {
        notifyImageUploadError();

        throw new Error(imageUploadErrorText);
      }
    },
    [imageUploadErrorText, notifyImageUploadError, uploadFile, validateImageFile],
  );

  const pickImageFile = React.useCallback(
    (callback: (value: string, meta?: Record<string, unknown>) => void) => {
      const editor = editorInstanceRef.current;
      const input = document.createElement("input");

      input.type = "file";
      input.accept = "image/*";

      input.addEventListener("change", () => {
        const file = input.files?.[0];

        if (!file || !editor) {
          return;
        }

        try {
          validateImageFile(file);
        } catch {
          return;
        }

        const reader = new FileReader();

        reader.addEventListener("load", () => {
          const result = reader.result;

          if (typeof result !== "string") {
            notifyImageUploadError();

            return;
          }

          const id = `blobid${Date.now()}`;
          const base64 = result.split(",")[1] ?? "";
          const blobCache = editor.editorUpload.blobCache;
          const blobInfo = blobCache.create(id, file, base64, file.name, file.name);

          blobCache.add(blobInfo);
          callback(blobInfo.blobUri(), { title: file.name, alt: file.name });
        });
        reader.addEventListener("error", notifyImageUploadError);
        reader.readAsDataURL(file);
      });

      input.click();
    },
    [notifyImageUploadError, validateImageFile],
  );

  const renderHtml = React.useCallback((html: string) => {
    const editor = editorInstanceRef.current;

    lastRenderedHtmlRef.current = html;

    if (editor) {
      isProgrammaticChangeRef.current = true;
      editor.setContent(html);
      editor.setDirty(false);
      isProgrammaticChangeRef.current = false;
    }

    setHasValue(Boolean(serializeTinyMceHtmlForApi(html)));
  }, []);

  React.useEffect(() => {
    if (lastRenderedHtmlRef.current !== initialHtml) {
      renderHtml(initialHtml);
    }
  }, [initialHtml, renderHtml]);

  React.useEffect(() => () => assignEditorRef(editorRef, null), [editorRef]);

  return (
    <FormControl
      data-test-id={"rich-text-editor-" + name}
      disabled={disabled}
      error={error}
      fullWidth
      variant="outlined"
    >
      <Box
        as="label"
        color={error ? "critical2" : "default2"}
        fontWeight="regular"
        size={1}
        position="absolute"
        htmlFor={id}
        zIndex="2"
        __top={9}
        __left={9}
      >
        {label}
      </Box>
      <div
        id={id}
        className={clsx(classes.tinymceRoot, classes.root, {
          [classes.rootErrorFocus]: isFocused && error,
          [classes.rootActive]: isFocused,
          [classes.rootDisabled]: disabled,
          [classes.rootError]: error,
          [classes.rootHasLabel]: label !== "",
          [classes.rootTyped]: isTyped,
        })}
      >
        <TinyMceEditor
          disabled={isReadOnly}
          initialValue={initialHtml}
          licenseKey={(import.meta.env.VITE_TINYMCE_LICENSE_KEY as string | undefined) || "gpl"}
          tinymceScriptSrc={`${import.meta.env.BASE_URL}tinymce/tinymce.min.js`}
          onBlur={() => {
            setIsFocused(false);
            onBlur?.();
          }}
          onEditorChange={(content, _editor) => {
            if (isProgrammaticChangeRef.current) {
              return;
            }

            const value = serializeTinyMceHtmlForApi(content);

            setHasValue(Boolean(value));

            // tinymce-react resyncs its content once on init if TinyMCE's own
            // serialization of initialValue doesn't match the raw string byte
            // for byte (near-guaranteed for any non-trivial HTML). That resync
            // fires this callback with no real user edit, before setDirty(false)
            // resets TinyMCE's own dirty flag - but it doesn't reset ours. Ignore
            // it until the editor has actually been focused at least once.
            if (!hasBeenFocusedRef.current) {
              return;
            }

            onChange?.(value);
          }}
          onFocus={() => {
            hasBeenFocusedRef.current = true;
            setIsFocused(true);
          }}
          onInit={(_event, editor) => {
            editorInstanceRef.current = editor;
            editor.setDirty(false);
            assignEditorRef(editorRef, {
              clear: async () => renderHtml(""),
              destroy: async () => editor.destroy(),
              render: async value => renderHtml(richTextToHtml(value)),
              save: async () => {
                await editor.editorUpload.uploadImages();

                return serializeTinyMceHtmlForApi(editor.getContent());
              },
            });
          }}
          init={{
            automatic_uploads: true,
            block_formats: "Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3",
            branding: false,
            content_style:
              "body { font-family: Helvetica, Arial, sans-serif; font-size: 14px; }",
            file_picker_callback: (callback, _value, meta) => {
              if (meta.filetype === "image") {
                pickImageFile(callback);
              }
            },
            file_picker_types: "image",
            image_title: true,
            images_file_types: "jpeg,jpg,jpe,jfi,jif,jfif,png,gif,bmp,webp,svg",
            images_reuse_filename: true,
            images_upload_handler: uploadTinyMceImage,
            menubar: false,
            paste_data_images: true,
            plugins: TINYMCE_PLUGINS,
            statusbar: false,
            toolbar: TINYMCE_TOOLBAR,
          }}
        />
      </div>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

const RichTextEditor = (props: RichTextEditorProps) => {
  const { enabled: isTinyMceRichTextEnabled } = useFlag("TINYMCE_RICH_TEXT");

  return isTinyMceRichTextEnabled ? (
    <TinyMceRichTextEditor {...props} />
  ) : (
    <EditorJsRichTextEditor {...props} />
  );
};

RichTextEditor.displayName = "RichTextEditor";
export default RichTextEditor;
